/**
 * search-api.ts — typed feature wrapper + mock for the global SearchBar (Story V10-K).
 *
 * The standalone tenant-wide hybrid (keyword + semantic) search. ⚠️ NOT the Brok agent.
 *
 * live mode → GET /search via the typed client (SearchResponseDto).
 * mock mode → a small deterministic fixture filtered by the query so the UI renders
 *             standalone without a backend (the VITE_DATA_MODE mock-fallback pattern).
 *
 * [Source: apps/web/src/features/customers/api/customers-api.ts] (wrapper + mock pattern)
 * [Source: docs/stories/v10-k-global-search.md AC5]
 */

import { apiClient } from '../../../lib/api-client';
import type { SearchHit, SearchHitType, SearchResponseDto } from '@brokly/shared';
import { SEARCH_HIT_TYPES } from '@brokly/shared';

/** Fetch live search results (GET /search). */
export function fetchSearch(q: string, limit?: number): Promise<SearchResponseDto> {
  return apiClient.search.query(q, { limit });
}

/**
 * DMS M12: the Policies-page Volltext + Semantisch toggle — `/search`
 * restricted to `scope: 'policy'` (dms/spec.md §5.2, §M10, §M12; WEB-1 fix,
 * wave dms-fixC — BOTH toggle positions call this, not just Semantisch). Only
 * 'police'-typed hits come back; the screen renders them as a ranked hit list
 * (title/subtitle/snippet + server-resolved `route`), same idiom as
 * GlobalSearchDialog.
 */
export function fetchPolicySemanticSearch(q: string, limit?: number): Promise<SearchResponseDto> {
  return apiClient.search.query(q, { limit, scope: 'policy' });
}

/**
 * ea/fix-search: the Ablage-page Volltext/Semantisch toggle — `/search` restricted to
 * `scope: 'document'` (mirrors `fetchPolicySemanticSearch`). Only 'dokument'-typed hits
 * come back (the document-content fulltext leg + the semantic document_chunk leg), routed
 * straight at `/ablage/:id`.
 */
export function fetchDocumentSemanticSearch(q: string, limit?: number): Promise<SearchResponseDto> {
  return apiClient.search.query(q, { limit, scope: 'document' });
}

// ── Mock fallback (VITE_DATA_MODE=mock) ────────────────────────────────────────

/** A small deterministic corpus for the standalone (no-backend) mock mode. */
const MOCK_HITS: SearchHit[] = [
  {
    type: 'kunde',
    id: 'k-basler',
    title: 'Basler Chemie AG',
    subtitle: 'CHE-101.234.567',
    snippet: null,
    score: 0.9,
    matchKind: 'keyword',
    route: '/customers/k-basler',
  },
  {
    type: 'police',
    id: 'p1',
    title: 'POL-2026-0001',
    subtitle: 'CHE-105.000.001',
    snippet: null,
    score: 1.0,
    matchKind: 'keyword',
    route: '/policen/p1',
  },
  {
    type: 'dokument',
    id: 'd1',
    title: 'Brokermandat_Basler.pdf',
    subtitle: null,
    snippet: 'Vollmacht zur Betreuung sämtlicher Versicherungsverträge …',
    score: 0.45,
    matchKind: 'semantic',
    route: '/inbox',
  },
  {
    type: 'kommunikation',
    id: 'c1',
    title: 'Offerte Betriebshaftpflicht',
    subtitle: 'info@basler-chemie.ch',
    snippet: null,
    score: 0.75,
    matchKind: 'keyword',
    route: '/inbox',
  },
];

/**
 * DMS M12: a dedicated policy-hit fixture for the Policies-page "Semantisch"
 * mock. Deliberately keyed to `p1`/`POL-339-2266` — the SAME id `usePolicyRows`'
 * mock corpus uses (Basler Chemie AG) — so a Semantisch search in mock mode
 * both matches something real on the grid AND its `route` lands on a policy
 * detail id `PoliceDetailScreen` actually resolves.
 */
const MOCK_POLICY_HITS: SearchHit[] = [
  {
    type: 'police',
    id: 'p1',
    title: 'POL-339-2266',
    subtitle: 'CSS · Betriebshaftpflicht',
    snippet: 'Deckungssumme CHF 5 Mio. für Betriebs- und Produktehaftpflicht …',
    score: 0.82,
    matchKind: 'semantic',
    route: '/policen/p1',
  },
];

function emptyCounts(): Record<SearchHitType, number> {
  return SEARCH_HIT_TYPES.reduce(
    (acc, t) => {
      acc[t] = 0;
      return acc;
    },
    {} as Record<SearchHitType, number>,
  );
}

/**
 * Build a mock SearchResponseDto for `q` by substring-filtering the fixture
 * (case-insensitive over title + subtitle). Deterministic; zero network.
 */
export function mockSearch(q: string): SearchResponseDto {
  const needle = q.trim().toLowerCase();
  const items = needle
    ? MOCK_HITS.filter(
        (h) =>
          h.title.toLowerCase().includes(needle) ||
          (h.subtitle?.toLowerCase().includes(needle) ?? false),
      )
    : [];
  const counts = emptyCounts();
  for (const h of items) counts[h.type] += 1;
  return { q, items, counts };
}

/**
 * DMS M12: mock fallback for the Policies-page "Semantisch" toggle
 * (`scope: 'policy'`) — substring-filters `MOCK_POLICY_HITS` the same way
 * `mockSearch` does, but only ever returns 'police'-typed hits (mirrors the
 * api's `scope=policy` leg-skipping behaviour).
 */
export function mockPolicySearch(q: string): SearchResponseDto {
  const needle = q.trim().toLowerCase();
  const items = needle
    ? MOCK_POLICY_HITS.filter(
        (h) =>
          h.title.toLowerCase().includes(needle) ||
          (h.subtitle?.toLowerCase().includes(needle) ?? false) ||
          (h.snippet?.toLowerCase().includes(needle) ?? false),
      )
    : [];
  const counts = emptyCounts();
  for (const h of items) counts[h.type] += 1;
  return { q, items, counts };
}

/**
 * ea/fix-search: a dedicated document-hit fixture for the Ablage-page "Semantisch" mock.
 * Deliberately keyed to `doc-avb-swica-leben`/`AVB-Swica-Lebensversicherung.pdf` — the SAME
 * id `DOCUMENTS_MOCK` (apps/web/src/features/documents/mock.ts) uses — so a Semantisch
 * search in mock mode both matches a real row on the grid AND its `route` lands on a
 * document id `DocumentViewerScreen` actually resolves.
 */
const MOCK_DOCUMENT_HITS: SearchHit[] = [
  {
    type: 'dokument',
    id: 'doc-avb-swica-leben',
    title: 'AVB-Swica-Lebensversicherung.pdf',
    subtitle: null,
    snippet: 'Deckungssumme CHF 5 Mio. für Betriebs- und Produktehaftpflicht …',
    score: 0.7,
    matchKind: 'semantic',
    route: '/ablage/doc-avb-swica-leben',
  },
];

/**
 * ea/fix-search: mock fallback for the Ablage-page "Semantisch" toggle (`scope: 'document'`)
 * — substring-filters `MOCK_DOCUMENT_HITS` the same way `mockPolicySearch` does, but only
 * ever returns 'dokument'-typed hits (mirrors the api's `scope=document` leg-skipping
 * behaviour).
 */
export function mockDocumentSearch(q: string): SearchResponseDto {
  const needle = q.trim().toLowerCase();
  const items = needle
    ? MOCK_DOCUMENT_HITS.filter(
        (h) =>
          h.title.toLowerCase().includes(needle) ||
          (h.subtitle?.toLowerCase().includes(needle) ?? false) ||
          (h.snippet?.toLowerCase().includes(needle) ?? false),
      )
    : [];
  const counts = emptyCounts();
  for (const h of items) counts[h.type] += 1;
  return { q, items, counts };
}
